import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AddCompanyModal from "../../../components/modals/AddCompanyModal";
import ChangeBrandModal from "../../../components/modals/ChangeBrandModal";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Loading from "../../../components/Loading";
import { DashboardLayout } from "../../../components/organisms/Layout";
import { selectRoles } from "../../../store/authSlice";
import Stepper from "../../../components/molecules/FormStepper/Stepper";
import { UseContextProvider } from "../../../components/molecules/FormStepper/StepperContext";
import Payment from "../../admin/Settings/Steps/Payments";
import Final from "../../admin/Settings/Steps/Final";
import CompanyDetails from "../../admin/Settings/Steps/CompanyDetails";
import RegionDetails from "../../admin/Settings/Steps/RegionDetails";
import BrandDetails from "../../admin/Settings/Steps/BrandDetails";
import LocationDetails from "../../admin/Settings/Steps/LocationDetails";
import { SettingsContextProvider } from "./SettingsContext";
import SlideOverPanel from "../../../components/atoms/SildeOverPanel/SlideOverPanel";
import Preview from "./Steps/Preview";

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [company] = useState({ companyName: "", companyId: "" });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editCompany] = useState(null);
  const [showChangeBrandModal, setShowChangeBrandModal] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedBusinessTypeId, setSelectedBusinessTypeId] = useState(null);
  const [warningStatus, setWarningStatus] = useState(false);
  const formTopRef = React.createRef();
  const [currentStep, setCurrentStep] = useState(1);
  const roles = useSelector(selectRoles);

  const steps = [
    "Business Details",
    "Brand",
    "Region",
    "Location",
    "Public Profile",
    "Complete",
  ];

  useEffect(() => {
    document.title = "HeyHire Portal-Settings";
    if (roles[0] !== "super-user") {
      navigate("/dashboard");
    }
  }, []);

  const handleBusinessTypeUpdate = (businessTypeId) => {
    setSelectedBusinessTypeId(businessTypeId);
  };

  const companyValidationCheck = () => {
    return (
      selectedBusinessTypeId !== null && selectedBusinessTypeId !== undefined
    );
  };

  const displayStep = (step) => {
    switch (step) {
      case 1:
        return (
          <CompanyDetails
            handleBusinessTypeUpdate={handleBusinessTypeUpdate.bind(this)}
          />
        );
      case 2:
        return <BrandDetails />;
      case 3:
        return <RegionDetails company={company} />;
      case 4:
        return <LocationDetails />;
      case 5:
        return <Payment />;
      case 6:
        return <Final />;
      default:
    }
  };

  const handleClick = async (direction) => {
    if (!companyValidationCheck()) {
      setWarningStatus(true);
      formTopRef.current.scrollIntoView({ behavior: "smooth" });
    } else {
      setWarningStatus(false);
      setLoading(true);

      try {
        let newStep = currentStep;
        direction === "next" ? newStep++ : newStep--;

        if (newStep > 0 && newStep <= steps.length) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          setCurrentStep(newStep);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <>
        {showModal && (
          <AddCompanyModal
            company={editCompany}
            title={
              editCompany
                ? t("companies.edit-company")
                : t("companies.add-a-company")
            }
            onClose={() => setShowModal(!showModal)}
            visible={showModal}
          />
        )}
        {showChangeBrandModal && (
          <ChangeBrandModal
            company={editCompany}
            title="Change Brand"
            onClose={() => setShowChangeBrandModal(!showChangeBrandModal)}
            visible={showChangeBrandModal}
          />
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div>
              <h2
                className="text-2xl font-bold text-gray-900"
                ref={formTopRef}
              >
                Settings
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Configure your business settings and manage your account
                preferences.
              </p>
            </div>
          </div>

          <div className="bg-white">
            <SettingsContextProvider>
              <div className="px-6 py-6 border-b border-gray-200">
                <Stepper
                  key={selectedBusinessTypeId ?? "none"}
                  steps={steps}
                  currentStep={currentStep}
                />
              </div>

              {warningStatus && (
                <div className="px-6 pt-6">
                  <div className="rounded-lg bg-yellow-50">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-yellow-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Selection required
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>
                            Choose a business type (Enterprise or Single
                            Business) before continuing.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <UseContextProvider>
                <div className="p-6">
                  <div className="bg-gray-50">
                    <div className="relative">
                      <div
                        className={`transition-opacity duration-300 ${loading ? "opacity-50" : "opacity-100"
                          }`}
                      >
                        <div className="max-w-3xl mx-auto p-6">
                          {displayStep(currentStep)}
                        </div>
                      </div>

                      {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                          <div className="flex flex-col items-center">
                            <Loading />
                            <span className="mt-4 text-sm text-gray-600">
                              Loading...
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </UseContextProvider>

              {isPanelOpen && (
                <SlideOverPanel
                  title="Business Details"
                  isOpen={isPanelOpen}
                  onClose={() => setIsPanelOpen(false)}
                >
                  <Preview />
                </SlideOverPanel>
              )}

              <div className="px-6 py-6 bg-gray-50">
                {currentStep !== steps.length && (
                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => handleClick("prev")}
                      className={`rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900
                        currentStep === 1 ? "opacity-50 cursor-not-allowed" : ""
                      `}
                      disabled={currentStep === 1}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handleClick("next")}
                      className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
                    >
                      {loading ? (
                        <i className="fad fa-circle-notch fa-spin" />
                      ) : (
                        "Next"
                      )}
                    </button>
                  </div>
                )}
              </div>
            </SettingsContextProvider>
          </div>
        </div>
      </>
    </DashboardLayout>
  );
};

export default Settings;
